/**
 * Pasos del tour guiado del vendedor (§6). Cada paso puede:
 *  - `route`: la ruta donde vive el paso (el tour navega solo antes de resaltar).
 *  - `target`: el `data-tour="..."` del elemento a resaltar (sin target → globo centrado).
 */
export interface TourStep {
  route?: string
  target?: string
  title: string
  body: string
}

export const TOUR_VERSION = 'vendor-v1'

export const VENDOR_TOUR: TourStep[] = [
  {
    route: '/',
    title: '¡Te damos la bienvenida! 👋',
    body: 'En un minuto te mostramos cómo usar la app para vender más. Podés saltar la guía cuando quieras y volver a verla desde el botón de ayuda (?).',
  },
  {
    route: '/',
    target: 'metrics',
    title: 'Tu rendimiento del mes',
    body: 'Tus ventas, pedidos cerrados y clics en tus links — siempre del mes en curso.',
  },
  {
    route: '/',
    target: 'ranking',
    title: 'Ranking de tu sucursal',
    body: 'Mirá cómo vas frente a tu equipo. Tu posición aparece resaltada, aunque no estés en el podio.',
  },
  {
    route: '/',
    target: 'nav-catalogo',
    title: 'Catálogo de productos',
    body: 'Desde acá explorás los productos: buscás por nombre y filtrás por Hot Items y Novedades.',
  },
  {
    route: '/catalogo',
    target: 'share',
    title: 'Compartir ⭐ (la función clave)',
    body: 'Tocá “Compartir” en cualquier producto: la app te arma tu link rastreable + un texto sugerido + banners para descargar y postear en tus redes o WhatsApp.',
  },
  {
    route: '/catalogo',
    target: 'nav-clientes',
    title: 'Tus clientes (CRM)',
    body: 'Tu cartera de clientes, con la información protegida por la empresa.',
  },
  {
    route: '/clientes',
    target: 'crm-search',
    title: 'Buscá y reactivá clientes',
    body: 'Buscá por nombre, DNI o email. Ves sus compras y, si dejaron un carrito abandonado, tenés un recordatorio listo para enviar por WhatsApp.',
  },
  {
    route: '/clientes',
    target: 'nav-novedades',
    title: 'Novedades',
    body: 'El contenido que prepara tu empresa para que publiques.',
  },
  {
    route: '/calendario',
    target: 'calendar',
    title: 'Contenido listo para publicar',
    body: 'Copiá el texto sugerido y descargá las imágenes de cada novedad, listas para tus redes.',
  },
  {
    route: '/',
    target: 'help',
    title: '¡Listo! 🎉',
    body: 'Eso es todo. Cuando quieras volver a ver esta guía, tocá el botón de ayuda (?) acá arriba.',
  },
]

export const ADMIN_TOUR_VERSION = 'admin-v1'

export const ADMIN_TOUR: TourStep[] = [
  {
    route: '/admin',
    title: 'Panel de administración 🛠️',
    body: 'Desde acá gestionás tu instancia y tu equipo. Te mostramos rápido cada sección. Podés saltar la guía y volver a verla con el botón de ayuda (?).',
  },
  {
    route: '/admin',
    target: 'admin-dashboard',
    title: 'Dashboard consolidado',
    body: 'El rendimiento de todo tu equipo en un solo lugar, con exportación a CSV.',
  },
  {
    route: '/admin',
    target: 'admin-vendedores',
    title: 'Gestión de vendedores',
    body: 'Alta, baja y edición de vendedores, e import masivo por CSV. Al crearlos se les envía el email de bienvenida.',
  },
  {
    route: '/admin',
    target: 'admin-colecciones',
    title: 'Hot Items y Novedades',
    body: 'Elegí qué colecciones VTEX se muestran como destacadas y su vigencia.',
  },
  {
    route: '/admin',
    target: 'admin-admins',
    title: 'Administradores',
    body: 'Gestioná quién más tiene acceso a este panel de administración.',
  },
  {
    route: '/admin',
    target: 'admin-marca',
    title: 'Marca (White Label)',
    body: 'Personalizá el logo, el color y el nombre de la app para tu tienda.',
  },
  {
    route: '/admin',
    target: 'admin-banners',
    title: 'Banners de Social Selling',
    body: 'Subí las imágenes descargables que tus vendedores usan al compartir productos.',
  },
  {
    route: '/admin',
    target: 'admin-calendario',
    title: 'Calendario de contenido',
    body: 'Prepará las publicaciones que tu equipo va a compartir.',
  },
  {
    route: '/admin',
    target: 'help',
    title: '¡Listo! 🎉',
    body: 'Cuando quieras volver a ver esta guía del panel, tocá el botón de ayuda (?) desde el admin.',
  },
]

/** Elige el tour según la ruta actual (admin vs vendedor). */
export function tourForPath(pathname: string): { steps: TourStep[]; version: string } {
  return pathname.startsWith('/admin')
    ? { steps: ADMIN_TOUR, version: ADMIN_TOUR_VERSION }
    : { steps: VENDOR_TOUR, version: TOUR_VERSION }
}
